<template>
  <div class="health-tracker-charts">
    <div class="grid grid-cols-1 gap-6">
      <!-- Blood Pressure Chart -->
      <div v-if="bloodPressureData.length > 0" class="bg-white rounded-lg border border-gray-200 p-4">
        <h4 class="text-lg font-medium text-gray-900 mb-4">Артериальное давление</h4>
        <div class="chart-container" style="position: relative; height: 300px;">
          <canvas ref="bloodPressureChart"></canvas>
        </div>
      </div>

      <!-- Weight Chart -->
      <div v-if="weightData.length > 0" class="bg-white rounded-lg border border-gray-200 p-4">
        <h4 class="text-lg font-medium text-gray-900 mb-4">Изменение веса</h4>
        <div class="chart-container" style="position: relative; height: 300px;">
          <canvas ref="weightChart"></canvas>
        </div>
      </div>

      <!-- Blood Sugar Chart -->
      <div v-if="bloodSugarData.length > 0" class="bg-white rounded-lg border border-gray-200 p-4">
        <h4 class="text-lg font-medium text-gray-900 mb-4">Уровень сахара в крови</h4>
        <div class="chart-container" style="position: relative; height: 300px;">
          <canvas ref="bloodSugarChart"></canvas>
        </div>
      </div>

      <!-- No Data Message -->
      <div v-if="bloodPressureData.length === 0 && weightData.length === 0 && bloodSugarData.length === 0" 
           class="text-center py-8">
        <svg class="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
        </svg>
        <p class="text-gray-600">Данных для построения графиков пока нет</p>
      </div>
    </div>
  </div>
</template>

<script>
import Chart from 'chart.js/auto'

export default {
  name: 'HealthTrackerCharts',
  props: {
    healthData: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      bloodPressureChart: null,
      weightChart: null,
      bloodSugarChart: null
    }
  },
  computed: {
    bloodPressureData() {
      return this.healthData
        .filter(entry => entry.metric_type === 'blood_pressure' && entry.systolic && entry.diastolic)
        .sort((a, b) => new Date(a.recorded_at) - new Date(b.recorded_at))
    },
    weightData() {
      return this.healthData
        .filter(entry => entry.metric_type === 'weight' && entry.value)
        .sort((a, b) => new Date(a.recorded_at) - new Date(b.recorded_at))
    },
    bloodSugarData() {
      return this.healthData
        .filter(entry => entry.metric_type === 'blood_sugar' && entry.value)
        .sort((a, b) => new Date(a.recorded_at) - new Date(b.recorded_at))
    }
  },
  watch: {
    healthData: {
      handler() {
        this.$nextTick(() => {
          this.createCharts()
        })
      },
      immediate: true
    }
  },
  mounted() {
    this.createCharts()
  },
  beforeUnmount() {
    this.destroyCharts()
  },
  methods: {
    destroyCharts() {
      if (this.bloodPressureChart) {
        this.bloodPressureChart.destroy()
        this.bloodPressureChart = null
      }
      if (this.weightChart) {
        this.weightChart.destroy()
        this.weightChart = null
      }
      if (this.bloodSugarChart) {
        this.bloodSugarChart.destroy()
        this.bloodSugarChart = null
      }
    },

    createCharts() {
      this.destroyCharts()
      
      this.$nextTick(() => {
        this.createBloodPressureChart()
        this.createWeightChart()
        this.createBloodSugarChart()
      })
    },

    createBloodPressureChart() {
      if (this.bloodPressureData.length === 0 || !this.$refs.bloodPressureChart) return

      const ctx = this.$refs.bloodPressureChart.getContext('2d')
      
      const labels = this.bloodPressureData.map(entry => 
        new Date(entry.recorded_at).toLocaleDateString('ru-RU', { 
          month: 'short', 
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      )

      this.bloodPressureChart = new Chart(ctx, {
        type: 'line',
        data: {
          labels,
          datasets: [
            {
              label: 'Систолическое',
              data: this.bloodPressureData.map(entry => entry.systolic),
              borderColor: '#EF4444',
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              tension: 0.1,
              fill: false
            },
            {
              label: 'Диастолическое',
              data: this.bloodPressureData.map(entry => entry.diastolic),
              borderColor: '#3B82F6',
              backgroundColor: 'rgba(59, 130, 246, 0.1)',
              tension: 0.1,
              fill: false
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            intersect: false,
            mode: 'index'
          },
          plugins: {
            legend: {
              position: 'top'
            },
            title: {
              display: false
            }
          },
          scales: {
            y: {
              beginAtZero: false,
              title: {
                display: true,
                text: 'мм рт.ст.'
              },
              grid: {
                color: 'rgba(0, 0, 0, 0.1)'
              }
            },
            x: {
              title: {
                display: true,
                text: 'Время'
              },
              grid: {
                color: 'rgba(0, 0, 0, 0.1)'
              }
            }
          }
        }
      })
    },

    createWeightChart() {
      if (this.weightData.length === 0 || !this.$refs.weightChart) return

      const ctx = this.$refs.weightChart.getContext('2d')
      
      const labels = this.weightData.map(entry => 
        new Date(entry.recorded_at).toLocaleDateString('ru-RU', { 
          month: 'short', 
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      )

      this.weightChart = new Chart(ctx, {
        type: 'line',
        data: {
          labels,
          datasets: [
            {
              label: 'Вес',
              data: this.weightData.map(entry => entry.value),
              borderColor: '#10B981',
              backgroundColor: 'rgba(16, 185, 129, 0.1)',
              tension: 0.1,
              fill: true
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            intersect: false,
            mode: 'index'
          },
          plugins: {
            legend: {
              position: 'top'
            },
            title: {
              display: false
            }
          },
          scales: {
            y: {
              beginAtZero: false,
              title: {
                display: true,
                text: 'кг'
              },
              grid: {
                color: 'rgba(0, 0, 0, 0.1)'
              }
            },
            x: {
              title: {
                display: true,
                text: 'Время'
              },
              grid: {
                color: 'rgba(0, 0, 0, 0.1)'
              }
            }
          }
        }
      })
    },

    createBloodSugarChart() {
      if (this.bloodSugarData.length === 0 || !this.$refs.bloodSugarChart) return

      const ctx = this.$refs.bloodSugarChart.getContext('2d')
      
      const labels = this.bloodSugarData.map(entry => 
        new Date(entry.recorded_at).toLocaleDateString('ru-RU', { 
          month: 'short', 
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      )

      this.bloodSugarChart = new Chart(ctx, {
        type: 'line',
        data: {
          labels,
          datasets: [
            {
              label: 'Сахар в крови',
              data: this.bloodSugarData.map(entry => entry.value),
              borderColor: '#F59E0B',
              backgroundColor: 'rgba(245, 158, 11, 0.1)',
              tension: 0.1,
              fill: true
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            intersect: false,
            mode: 'index'
          },
          plugins: {
            legend: {
              position: 'top'
            },
            title: {
              display: false
            }
          },
          scales: {
            y: {
              beginAtZero: false,
              title: {
                display: true,
                text: 'ммоль/л'
              },
              grid: {
                color: 'rgba(0, 0, 0, 0.1)'
              }
            },
            x: {
              title: {
                display: true,
                text: 'Время'
              },
              grid: {
                color: 'rgba(0, 0, 0, 0.1)'
              }
            }
          }
        }
      })
    }
  }
}
</script>

<style scoped>
.chart-container {
  width: 100%;
  height: 300px;
}
</style> 